import { BookOpenText, GitCompareArrows, LogOut, Table2 } from "lucide-react";
import { Link, useRouterState } from "@tanstack/react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAuthSession } from "@/components/AuthGate";
import { cn } from "@/lib/cn";
import {
  type CompareSearchInput,
  type VersionsSearchInput,
  getLastCompareSearch,
  getLastVersionSearch,
  rememberCompareSearch,
  rememberVersionSearch,
} from "@/lib/last-route-search";

// Persistent top bar: brand, Chart compare and Views, API docs.
export function TopBar(): JSX.Element {
  const auth = useAuthSession();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const search = useRouterState({ select: (s) => s.location.search });
  if (pathname === "/compare") {
    rememberCompareSearch(search as CompareSearchInput);
  } else if (pathname === "/versions") {
    rememberVersionSearch(search as VersionsSearchInput);
  }
  const showLogout =
    auth.authenticated
    && (auth.mode === "basic" || auth.mode === "oidc" || auth.mode === "both");
  return (
    <header className="sticky top-0 z-20 border-b border-border bg-card/80 backdrop-blur">
      <div className="mx-auto flex max-w-screen-2xl items-center justify-between gap-4 px-4 py-3">
        <div className="flex items-center gap-6">
          <Link to="/" className="flex items-center gap-3 outline-none">
            <img
              src="/helmdiver-logo-192.png"
              alt=""
              className="size-7 rounded"
            />
            <span className="text-base font-semibold tracking-tight text-foreground">
              helmdiver
            </span>
            <Badge variant="outline" className="hidden font-mono md:inline-flex">
              v{__APP_VERSION__}
            </Badge>
          </Link>
          <nav
            className="flex items-center gap-1 rounded-lg border border-border/60 bg-brand-surface/40 p-1 text-sm"
            aria-label="Main"
          >
            <Link
              to="/compare"
              search={getLastCompareSearch()}
              className={navClass(pathname === "/compare")}
            >
              <GitCompareArrows className="size-4 shrink-0" aria-hidden />
              Browser
            </Link>
            <Link
              to="/versions"
              search={getLastVersionSearch()}
              className={navClass(pathname === "/versions")}
            >
              <Table2 className="size-4 shrink-0" aria-hidden />
              Views
            </Link>
          </nav>
        </div>
        <div className="flex items-center gap-2">
          {showLogout ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void auth.logout()}
            >
              <LogOut />
              Logout
            </Button>
          ) : null}
          <Button variant="ghost" size="sm" asChild>
            <a href="/api/docs" target="_blank" rel="noreferrer">
              <BookOpenText />
              API docs
            </a>
          </Button>
        </div>
      </div>
    </header>
  );
}

function navClass(active: boolean): string {
  return cn(
    "inline-flex items-center gap-2 rounded-md px-3 py-1.5 font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring",
    active
      ? "bg-brand-surface text-brand-accent shadow-sm"
      : "text-muted-foreground hover:bg-brand-surface/60 hover:text-foreground",
  );
}
